/**
 * ExitList Component
 * 
 * Displays available exits from the current room with locked/unlocked status.
 * Provides accessible navigation controls for moving between rooms.
 * 
 * Accessibility:
 * - Semantic nav element
 * - Clear button labels
 * - Disabled state for locked doors
 * - ARIA labels for screen readers
 */

import React from 'react';
import { Button } from '@components/common/Button';
import styles from './ExitList.module.scss';

export interface Exit {
  /** Room ID this exit leads to */
  roomId: number;
  /** Whether this exit is locked (room already visited) */
  isLocked: boolean;
}

export interface ExitListProps {
  /** Array of available exits */
  exits: Exit[];
  /** Callback when an exit is selected */
  onSelectExit: (roomId: number) => void;
}

/**
 * ExitList component
 * 
 * @example
 * ```tsx
 * <ExitList 
 *   exits={[{ roomId: 1, isLocked: false }, { roomId: 2, isLocked: true }]}
 *   onSelectExit={handleMove}
 * />
 * ```
 */
export const ExitList: React.FC<ExitListProps> = ({ exits, onSelectExit }) => {
  if (exits.length === 0) {
    return (
      <nav className={styles.exitList} aria-label="Room exits">
        <h3 className={styles.title}>Available Exits</h3>
        <p className={styles.noExits}>No exits available</p>
      </nav>
    );
  }

  return (
    <nav className={styles.exitList} aria-label="Room exits">
      <h3 className={styles.title}>Available Exits</h3>
      <ul className={styles.list}>
        {exits.map(exit => (
          <li key={exit.roomId}>
            <Button
              onClick={() => onSelectExit(exit.roomId)}
              disabled={exit.isLocked}
              variant={exit.isLocked ? 'secondary' : 'primary'}
              aria-label={`Go to room ${exit.roomId}${exit.isLocked ? ' (locked)' : ''}`}
            >
              Room {exit.roomId} {exit.isLocked && '🔒'}
            </Button>
          </li>
        ))}
      </ul>
    </nav>
  );
};